// src/hooks/useEditProfileForm.ts
import { useEffect, useMemo } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { patchAPI } from '../../services/api.service';
import { useProfileData } from './useProfileData';
import { toast } from 'react-toastify';

type FormValues = {
  firstname: string;
  lastname: string;
  email: string;
};

export function useEditProfileForm() {
  const { profile, loading, error: profileError, refetch } = useProfileData();

  const {
    register,
    control,               // <-- add control
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting, isDirty, isValid }
  } = useForm<FormValues>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: { firstname: '', lastname: '', email: '' }
  });

  const rules = useMemo(() => ({
    firstname: {
      required: 'First name is required',
      minLength: { value: 2, message: 'First name must be at least 2 characters' },
      maxLength: { value: 50, message: 'First name max 50 characters' },
      pattern: { value: /^[A-Za-z\s'-]+$/, message: 'Only letters, spaces, apostrophes and hyphens allowed' }
    },
    lastname: {
      required: 'Last name is required',
      minLength: { value: 1, message: 'Last name must be at least 1 character' },
      maxLength: { value: 50, message: 'Last name max 50 characters' },
      pattern: { value: /^[A-Za-z\s'-]+$/, message: 'Only letters, spaces, apostrophes and hyphens allowed' }
    },
    email: {
      required: 'Email is required',
      pattern: { value: /^\S+@\S+\.\S+$/, message: 'Invalid email address' }
    }
  }), []);

  useEffect(() => {
    if (profile) {
      console.log('[useEditProfileForm] reset with profile =>', profile);
      reset({
        firstname: profile.firstname ?? '',
        lastname: profile.lastname ?? '',
        email: profile.email ?? ''
      }, { keepDefaultValues: true });
    }
  }, [profile, reset]);

  const onSubmit = async (data: FormValues) => {
    try {
      const userStr = localStorage.getItem('user');
      const userObj = userStr ? JSON.parse(userStr) : null;
      const userId = userObj?.user?.id ?? userObj?.id;

      const payload: Partial<FormValues> = {};
      if (data.firstname !== (profile?.firstname ?? '')) payload.firstname = data.firstname;
      if (data.lastname !== (profile?.lastname ?? '')) payload.lastname = data.lastname;
      if (data.email !== (profile?.email ?? '')) payload.email = data.email;

      if (Object.keys(payload).length === 0) return;

      const resp = await patchAPI('/auth/me', payload, userId ? { userId } : undefined);
      if (resp.status === 200) {
        await refetch?.();
        toast.success('Profile updated successfully');
      } else {
        toast.error(String(resp.error) ?? 'Failed to update profile');
      }
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to update profile');
    }
  };

  return {
    profile,
    loading,
    error: profileError,
    register,
    control,               // <-- return control
    handleSubmit,
    errors,
    isSubmitting,
    onSubmit,
    isDirty,
    isValid,
    watch,
    validationRules: rules
  };
}
